import { SUPPORTED_LOCALES, type Locale } from '@syncanix/shared';
import { Avatar, cn } from '@syncanix/ui';
import { Check, CircleUserRound, Hash, LogOut, Moon, SlidersHorizontal, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useT } from '@/core/providers/I18nProvider';
import { useRovingMenu } from '@/hooks/useRovingMenu';

import type { ControlMode } from '@/core/providers/ControlModeProvider';
import type { ReactElement } from 'react';

export interface UserMenuUser {
  name: string;
  email: string;
  picture?: string | null;
}

export interface UserMenuProps {
  user: UserMenuUser;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  /**
   * How much control the surfaces that support it should offer. A reader
   * preference that spans pages, which is why it is switched from the chrome
   * rather than from any one page — see `ControlModeProvider`.
   */
  controlMode: ControlMode;
  onToggleControlMode: () => void;
  /** Navigate to the dedicated /account page (SYN-148). */
  onAccount: () => void;
  onSignOut: () => void;
  className?: string;
}

/**
 * Right-side user dropdown in the top bar. The trigger is the avatar alone
 * (the picture, or an initials tile as fallback); the display name + email
 * live inside the opened menu, which also exposes the language picker, theme
 * toggle, the Simple ⇄ Advanced control-depth switch, and sign-out. Closes on
 * outside click, Escape, or selecting an action — the two presentation
 * preferences (theme, controls) deliberately leave it open, since the row that
 * changed is the row that was clicked.
 *
 * All chrome copy resolves through `useT()`; the language picker lists every
 * `SUPPORTED_LOCALES` entry by its **native** name (shown in its own script,
 * never translated) and calls `setLocale`, which re-runs the `<html dir>`
 * effect so an RTL choice flips the whole app immediately.
 */
export function UserMenu({
  user,
  theme,
  onToggleTheme,
  controlMode,
  onToggleControlMode,
  onAccount,
  onSignOut,
  className,
}: UserMenuProps): ReactElement {
  const { t, locale, setLocale, numerals, setNumerals } = useT();
  // The Eastern Arabic-Indic numeral opt-in (ADR-0141) only affects Arabic —
  // hide the toggle for every other locale, where it is a no-op.
  const showNumerals = locale === 'ar' || locale.startsWith('ar-');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // WAI-ARIA menu keyboard contract: focus the first item on open, arrow keys
  // cycle, Home/End jump, roving tabindex. (Open/close + Escape stay below.)
  useRovingMenu(menuRef, open);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('usermenu.accountMenu', { name: user.name })}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        // The avatar is the whole trigger — it needs a visible hover/open
        // affordance of its own (a hairline ring that strengthens on hover).
        className="inline-flex items-center gap-2 rounded-full p-1 text-sm transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {/* The shared Bedrock avatar, `accent` because this is the signed-in
            user — principle 02's one place a gradient marks identity. It also
            derives its own initials, which fixes two defects the local copy
            here carried: `charAt(0)` halves an astral-plane first letter into
            a lone surrogate, and `toUpperCase()` is locale-blind. The button
            already carries the accessible name, so the avatar is hidden. */}
        <Avatar
          name={user.name || user.email}
          {...(user.picture != null && user.picture !== '' ? { src: user.picture } : {})}
          accent
          aria-hidden="true"
          role="presentation"
          aria-label={undefined}
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('usermenu.accountActions')}
          // p-1 inset + rounded rows: the hover fill sits inside the panel's
          // radius instead of bleeding square-edged to the border.
          // `rounded-md` (8px), not the panel's 10: a FLOATING menu takes the
          // tighter corner in the spec, and the two Radix overlay primitives
          // (`DropdownMenu`, `Popover`) already do. This hand-rolled sibling was
          // the one out of step — see the radius note in `styles.css`.
          className="absolute end-0 z-40 mt-2 w-56 rounded-md border border-hairline bg-surface p-1 shadow-elevation-2 motion-safe:animate-fade-in"
        >
          <div className="px-2 py-1.5" role="none">
            <p className="truncate text-sm font-semibold text-foreground">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
          <div className="-mx-1 my-1 h-px bg-hairline" role="separator" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAccount();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            <CircleUserRound aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            {t('account.title')}
          </button>
          <div className="-mx-1 my-1 h-px bg-hairline" role="separator" />

          <div className="px-2 pb-1 pt-1.5" role="none">
            <p className="font-mono text-label uppercase text-muted-foreground">
              {t('usermenu.language')}
            </p>
          </div>
          <div role="group" aria-label={t('usermenu.language')}>
            {SUPPORTED_LOCALES.map((meta) => {
              const active = meta.code === locale;
              return (
                <button
                  key={meta.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  lang={meta.code}
                  onClick={() => {
                    selectLocale(meta.code, setLocale, setOpen);
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className={cn(active && 'font-medium')}>{meta.nativeName}</span>
                  {active ? (
                    <Check aria-hidden="true" className="h-4 w-4 text-primary-strong" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="-mx-1 my-1 h-px bg-hairline" role="separator" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onToggleTheme();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            {theme === 'dark' ? (
              <Moon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Sun aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            )}
            <span>{t('usermenu.theme')}</span>
            <span className="ms-auto text-xs text-muted-foreground">
              {theme === 'dark' ? t('usermenu.themeDark') : t('usermenu.themeLight')}
            </span>
          </button>
          {/* Simple ⇄ Advanced. Same row shape as Theme deliberately: both are
              two-state presentation preferences that persist per browser and
              affect every surface at once, so they should not read as two
              different kinds of control. Like Theme, it does NOT close the menu
              — the row it changes is the row you just clicked. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onToggleControlMode();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            <span>{t('usermenu.controls')}</span>
            <span className="ms-auto text-xs text-muted-foreground">
              {controlMode === 'advanced'
                ? t('usermenu.controlsAdvanced')
                : t('usermenu.controlsSimple')}
            </span>
          </button>
          {showNumerals ? (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={numerals === 'eastern'}
              onClick={() => {
                setNumerals(numerals === 'eastern' ? 'western' : 'eastern');
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            >
              <Hash aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
              <span>{t('usermenu.numerals')}</span>
              <span className="ms-auto text-xs text-muted-foreground">
                {numerals === 'eastern'
                  ? t('usermenu.numeralsEastern')
                  : t('usermenu.numeralsWestern')}
              </span>
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-danger-foreground hover:bg-danger/10 focus-visible:bg-danger/10 focus-visible:outline-none"
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {t('usermenu.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function selectLocale(
  code: Locale,
  setLocale: (locale: Locale) => void,
  setOpen: (open: boolean) => void,
): void {
  setLocale(code);
  setOpen(false);
}
